import create from 'zustand';

import {
  BILLING_MODE_MONTH,
  DEFAULT_USER_CURRENCY,
  INTERVAL_COUNT_FOR_BILLING_MONTH,
} from '@/config/appConstants';
import { IntervalBillingMode, Currency, ProductTypes, BillingMode } from '@/types';
import { getIntervalCountByBillingMode } from '@/utils/helpers';

type SelectedUserProductData = {
  selectedProduct: ProductTypes | null;
  selectedProductCurrency: Currency;
  selectedProductIntervalBillingMode: IntervalBillingMode;
  selectedProductBillingMode: BillingMode;
};

type UserProductHook = {
  isSelectedProductCurrency: boolean;
  selectedProduct: ProductTypes | null;
  selectedProductCurrency: Currency;
  selectedProductIntervalBillingMode: IntervalBillingMode;
  selectedProductBillingMode: BillingMode;
  getSelectedUserProduct: () => SelectedUserProductData;
  isSelectedProductCurrencySet: () => boolean;
  setSelectedProduct: (product: ProductTypes | null, intervalCount?: IntervalBillingMode) => void;
  setSelectedProductBillingMode: (billingMode: BillingMode) => void;
  setSelectedProductCurrency: (currency: Currency) => void;
};

export const useUserProducts = create<UserProductHook>((set, get) => ({
  isSelectedProductCurrency: false,
  selectedProduct: null,
  selectedProductCurrency: DEFAULT_USER_CURRENCY,
  selectedProductIntervalBillingMode: INTERVAL_COUNT_FOR_BILLING_MONTH,
  selectedProductBillingMode: BILLING_MODE_MONTH,

  getSelectedUserProduct: (): SelectedUserProductData => {
    return {
      selectedProduct: get().selectedProduct,
      selectedProductCurrency: get().selectedProductCurrency,
      selectedProductIntervalBillingMode: get().selectedProductIntervalBillingMode,
      selectedProductBillingMode: get().selectedProductBillingMode,
    };
  },

  setSelectedProduct: (product: ProductTypes | null, intervalCount?: IntervalBillingMode) => {
    if (product === get().selectedProduct) return;
    set(() => ({
      isSelectedProductCurrency: true,
      selectedProduct: product,
      selectedProductIntervalBillingMode: intervalCount
        ? intervalCount
        : get().selectedProductIntervalBillingMode,
    }));
  },

  setSelectedProductBillingMode: (billingMode: BillingMode) => {
    const selectedProductIntervalBillingMode = getIntervalCountByBillingMode(billingMode);
    set(() => ({
      selectedProductBillingMode: billingMode,
      selectedProductIntervalBillingMode: selectedProductIntervalBillingMode,
    }));
  },

  setSelectedProductCurrency: (currency: Currency) => {
    if (currency === get().selectedProductCurrency) return;

    set(() => ({
      isSelectedProductCurrency: true,
      selectedProductCurrency: currency,
    }));
  },

  isSelectedProductCurrencySet: (): boolean => {
    return get().isSelectedProductCurrency;
  },
}));
